import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { toast } from "sonner";
import { ArrowLeftRight, CheckCircle2, Clock, MapPin, User as UserIcon, X, RefreshCw } from "lucide-react";

const formatDate = (d) => new Date(d).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit", year: "numeric" });

export default function ShiftExchangePage() {
  const { user, isManager } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/schedules/exchange-board");
      setItems(data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Konnte nicht laden");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const claim = async (shift_id) => {
    try { await api.post("/schedules/swap-claim", { shift_id }); toast.success("Du hast die Schicht beansprucht – wartet auf Genehmigung"); await load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };
  const retract = async (shift_id) => {
    if (!window.confirm("Angebot zurückziehen?")) return;
    try { await api.post("/schedules/swap-cancel", { shift_id }); toast.success("Zurückgezogen"); await load(); }
    catch (err) { toast.error("Fehler"); }
  };
  const approve = async (s) => {
    if (!s.swap_claim_user_id) { toast.error("Noch niemand hat sich gemeldet"); return; }
    try { await api.post("/schedules/swap-approve", { shift_id: s.id, new_user_id: s.swap_claim_user_id }); toast.success("Tausch genehmigt"); await load(); }
    catch (err) { toast.error("Fehler"); }
  };

  const mine = items.filter(s => s.swap_request_user_id === user.id);
  const others = items.filter(s => s.swap_request_user_id !== user.id);

  return (
    <div className="space-y-6" data-testid="exchange-root">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="overline">Team</div>
          <h2 className="font-heading text-2xl sm:text-3xl font-bold">Schichttausch-Börse</h2>
          <p className="text-sm text-slate-500 mt-1">Offene Tauschangebote deiner Kolleg:innen – Klicke „Übernehmen" und ein Admin bestätigt.</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="exchange-refresh" className="gap-2 rounded-full">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Neu laden
        </Button>
      </div>

      {mine.length > 0 && (
        <section className="space-y-2">
          <h3 className="font-heading font-bold text-lg">Deine Angebote</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {mine.map(s => (
              <ShiftCard
                key={s.id}
                shift={s}
                mine
                onRetract={() => retract(s.id)}
                onApprove={isManager ? () => approve(s) : null}
              />
            ))}
          </div>
        </section>
      )}

      <section className="space-y-2">
        <h3 className="font-heading font-bold text-lg">Verfügbare Schichten</h3>
        {others.length === 0 ? (
          <Card className="border-border/60 border-dashed">
            <CardContent className="p-10 text-center text-slate-500 text-sm">
              <ArrowLeftRight className="w-8 h-8 mx-auto text-slate-300 mb-2" />
              Aktuell keine Tauschangebote. Wenn ein:e Kolleg:in eine Schicht anbietet, erscheint sie hier automatisch.
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {others.map(s => (
              <ShiftCard
                key={s.id}
                shift={s}
                onClaim={s.swap_claim_user_id ? null : () => claim(s.id)}
                claimed={!!s.swap_claim_user_id}
                onApprove={isManager && s.swap_claim_user_id ? () => approve(s) : null}
              />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function ShiftCard({ shift, mine, claimed, onClaim, onRetract, onApprove }) {
  const s = shift;
  const durMin = (() => {
    const [sh, sm] = (s.start_time || "0:0").split(":").map(Number);
    const [eh, em] = (s.end_time || "0:0").split(":").map(Number);
    let m = (eh * 60 + em) - (sh * 60 + sm);
    if (m < 0) m += 24 * 60;
    return m;
  })();
  const dur = `${Math.floor(durMin / 60)}:${String(durMin % 60).padStart(2, "0")}h`;
  const initials = ((s.offerer?.first_name?.[0] || "?") + (s.offerer?.last_name?.[0] || "")).toUpperCase();
  return (
    <Card data-testid={`exchange-shift-${s.id}`} className="border-border/60 card-hover" style={{ borderTop: `3px solid ${s.location?.color || "#1D4ED8"}` }}>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-heading font-bold text-base">{formatDate(s.date)}</div>
            <div className="text-sm text-slate-500 flex items-center gap-1.5 mt-0.5"><Clock className="w-3.5 h-3.5" /> {s.start_time} – {s.end_time} <span className="text-slate-400">·</span> {dur}</div>
          </div>
          {mine && <Badge variant="secondary">Dein Angebot</Badge>}
          {claimed && !mine && <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Beansprucht</Badge>}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <MapPin className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-slate-600 dark:text-slate-300">{s.location?.name || "—"}</span>
          {s.area && <><span className="text-slate-300">·</span><span className="text-slate-500">{s.area}</span></>}
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Avatar className="w-7 h-7"><AvatarFallback className="bg-blue-100 text-blue-700 text-xs font-bold">{initials}</AvatarFallback></Avatar>
          <div className="text-sm">
            <div className="font-medium">{s.offerer?.first_name} {s.offerer?.last_name}</div>
            <div className="text-xs text-slate-500">{s.offerer?.position}</div>
          </div>
        </div>
        {s.notes && <div className="text-xs text-slate-500 italic border-l-2 border-border/60 pl-2">{s.notes}</div>}
        <div className="flex gap-2 pt-1">
          {mine && (
            <Button data-testid={`exchange-retract-${s.id}`} size="sm" variant="outline" className="w-full gap-1" onClick={onRetract}><X className="w-3.5 h-3.5" /> Zurückziehen</Button>
          )}
          {!mine && onClaim && (
            <Button data-testid={`exchange-claim-${s.id}`} size="sm" className="w-full gap-1" onClick={onClaim}><ArrowLeftRight className="w-3.5 h-3.5" /> Übernehmen</Button>
          )}
          {onApprove && (
            <Button data-testid={`exchange-approve-${s.id}`} size="sm" variant="outline" className="w-full gap-1" onClick={onApprove}><CheckCircle2 className="w-3.5 h-3.5" /> Genehmigen</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
